import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogActions } from "@mui/material";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { BonusApplication } from "../../../../services/bonus/types";

interface UpdateReviewerDialogProps {
  open: boolean;
  onClose: () => void;
  application: BonusApplication | null;
}

const UpdateReviewerDialog: React.FC<UpdateReviewerDialogProps> = ({
  open,
  onClose,
  application,
}) => {
  const navigate = useNavigate();

  if (!application) return null;

  const handleUpdateReviewer = () => {
    const { applicationType, applicationId } = application;

    // Map application types to their routes
    const routeMap: Record<string, string> = {
      CCM1: `/CCM/${applicationId}?mode=update-reviewer`,
      CCM2: `/CCM/${applicationId}?mode=update-reviewer`,
      CCM3: `/CCM/${applicationId}?mode=update-reviewer`,
      CCM3Manager: `/DCCM/${applicationId}?mode=update-reviewer`,
      CM3: `/CM3PM/${applicationId}?mode=update-reviewer`,
      PM: `/CM3PM/${applicationId}?mode=update-reviewer`,
      DCCM: `/DCCM/${applicationId}?mode=update-reviewer`,
      SPM: `/SPM/${applicationId}?mode=update-reviewer`,
    };

    const route = routeMap[applicationType];
    
    if (route) {
      navigate(route);
      onClose();
    } else {
      console.error(`No route found for application type: ${applicationType}`);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        className: "rounded-lg",
      }}
    >
      <div className="flex items-center justify-between p-6 border-b border-gray-200">
        <DialogTitle className="text-xl font-semibold text-gray-900 p-0">
          Update Reviewer
        </DialogTitle>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition-colors"
        >
          <XMarkIcon className="w-6 h-6" />
        </button>
      </div>
      <DialogContent className="p-6">
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-blue-800 font-medium mb-2">Application Details</p>
            <div className="space-y-1">
              <p className="text-sm text-blue-700">
                <span className="font-medium">ID:</span> {application.applicationId}
              </p>
              <p className="text-sm text-blue-700">
                <span className="font-medium">Type:</span> {application.applicationType}
              </p>
              <p className="text-sm text-blue-700">
                <span className="font-medium">Status:</span> {application.status?.replace(/_/g, ' ') || 'N/A'}
              </p>
              <p className="text-sm text-blue-700">
                <span className="font-medium">Applicant:</span> {application.employeeName || application.submittedBy}
              </p>
            </div>
          </div>

          <div className="text-sm text-gray-600">
            <p className="mb-2">
              You will be redirected to the application details page where you can update the reviewer assignments for this application.
            </p>
            <p className="font-medium text-gray-700">
              Available reviewers will be shown based on the application type and current status.
            </p>
          </div>
        </div>
      </DialogContent>
      <DialogActions className="p-6 border-t border-gray-200">
        <button
          onClick={onClose}
          className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleUpdateReviewer}
          className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
        >
          Continue to Update Reviewer
        </button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateReviewerDialog;